// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the encryption seam, wired and off
//  ---------------------------------------------------------------------------
//  Nothing in this phase is stored as ciphertext. What ships here is the FORMAT
//  those documents will be stored in, fixed now, while fixing it is still free.
//  The moment one real envelope exists on one real device, the format is
//  permanent — so the version byte, the algorithm name, the IV size and the
//  field order are decided in this file and pinned in tests/crypto.test.js by a
//  literal envelope that must open forever.
//
//  THE SEAM IS OFF AND IT COSTS NOTHING. mdSealAll returns the array it was
//  handed — the same array, not a copy — when sealing is off. A seam that
//  walked 5,000 documents to decide it had nothing to do would be the exact
//  defect this phase was written to retire, reintroduced by the thing meant to
//  prepare for the next one, and the next person to profile a boot would delete
//  it. It has to be free to survive until it is needed.
//
//  OPENING IS DECIDED BY THE VALUE, NOT BY THE FLAG. mdOpen looks at what it
//  was handed. If a flag decided, then turning the seam off would strand every
//  document written while it was on, and "wired and off" would be a one-way
//  door rather than a switch. This is the property that makes shipping it early
//  safe.
//
//  AND IT REFUSES RATHER THAN GUESSES. A v2 envelope, a truncated one, one
//  under a key this device does not have — all `null`. Handing back the raw
//  bytes "in case they were plaintext" is how ciphertext reaches a screen as
//  though a person had written it.
//
//  ── WHY THE ACCOUNT KEY IS RANDOM AND NOT DERIVED FROM A PASSKEY ──────────
//  WebAuthn's PRF extension gives a secret per CREDENTIAL, and the RP ID it is
//  bound to is the exact request host (server/auth.js:84). A key derived from
//  PRF is therefore a different key on the laptop and on the phone, and a
//  different key again on staging and on production. The second device would
//  open nothing.
//
//  So: ONE random 256-bit account key, generated once, wrapped once per way in.
//  A credential's PRF output wraps it. The recovery code wraps it again. Every
//  wrapping opens the same key, adding a device is adding a wrapping, and
//  losing a device is deleting one. The server holds the wrapped blobs and can
//  open none of them.
//
//  The HKDF `info` binds a wrapping to the path that made it, cryptographically
//  rather than by a string compare: a blob wrapped for recovery cannot be
//  opened down a credential's path even if the two secrets somehow met.
//
//  ── WHAT MUST BE ANSWERED BEFORE THE FLAG IS FLIPPED ──────────────────────
//  Search. C3's FTS5 index is built by triggers over `docs.value`, so an engine
//  holding sealed values indexes ciphertext and mdSearch finds nothing. That is
//  not a defect in this file — it is the honest cost of encryption at rest, and
//  it is the reason this phase stops at the seam. The answer is a client-side
//  index the worker builds from opened values, and it is a story, not a patch.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const MD_CRYPTO_VERSION = 1;
  const MD_CRYPTO_ALG = 'A256GCM';

  /* 12 bytes is the IV length AES-GCM is specified around; anything else costs
     an extra derivation inside every implementation and buys nothing. */
  const IV_BYTES = 12;
  const SALT_BYTES = 16;

  /* No I, no O, no U, no 0, no 1. This is read off one screen and typed into a
     device that holds none of the person's data, possibly over a phone call.
     31 symbols × 25 characters is about 124 bits. */
  const CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTVWXYZ';
  const CODE_LENGTH = 25;
  const CODE_GROUP = 5;

  /* Two pieces of state, and they are deliberately not one. `key` is whether
     this device CAN open; `sealing` is whether it SHOULD seal. Disarming
     sealing without dropping the key is what a rollback looks like — new
     writing goes down in plaintext and everything already written still
     opens. */
  let key: any = null;
  let sealing = false;

  const subtle = () => (g.crypto && g.crypto.subtle) || null;

  function randomBytes(n: number): Uint8Array {
    const out = new Uint8Array(n);
    g.crypto.getRandomValues(out);
    return out;
  }

  /* ── base64url, the same plumbing md-sync.js does for WebAuthn ──────────── */
  function toB64u(bytes: Uint8Array): string {
    let s = '';
    for (const b of bytes) s += String.fromCharCode(b);
    return g.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fromB64u(text: string): Uint8Array | null {
    try {
      const p = text.replace(/-/g, '+').replace(/_/g, '/');
      const bin = g.atob(p + '='.repeat((4 - (p.length % 4)) % 4));
      const out = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out;
    } catch {
      return null;                    // not base64 at all; the caller refuses
    }
  }

  /* ── is this an envelope? ─────────────────────────────────────────────────
     Cheap, and on the head of the string rather than a parse: this runs once
     per document on every load of the account, and JSON.parse of 5,000 bodies
     to answer "probably not" is a pass over the whole account.

     It matches the FAMILY, not this version — so a v2 envelope is recognised
     as something this reader must refuse, rather than mistaken for a person's
     JSON and handed to the app intact. */
  const ENVELOPE_HEAD = /^\{"v":(\d+),"alg":"([A-Za-z0-9]+)","iv":"/;

  function isEnvelope(raw: unknown): boolean {
    return typeof raw === 'string' && ENVELOPE_HEAD.test(raw);
  }

  /* ── sealing ─────────────────────────────────────────────────────────────
     A non-string passes through untouched: `null` is how the mirror in
     md-storage.js spells a removal, and encrypting a deletion would turn a key
     that should disappear into a document that cannot be read. */
  async function mdSeal(raw: unknown): Promise<unknown> {
    if (!sealing || typeof raw !== 'string' || !key || !subtle()) return raw;
    try {
      const iv = randomBytes(IV_BYTES);
      const ct = await subtle().encrypt(
        { name: 'AES-GCM', iv },
        key,
        new TextEncoder().encode(raw),
      );
      /* This exact key order is the format. tests/crypto.test.js asserts it,
         and ENVELOPE_HEAD above depends on it. */
      return `{"v":${MD_CRYPTO_VERSION},"alg":"${MD_CRYPTO_ALG}","iv":"${toB64u(iv)}","ct":"${toB64u(new Uint8Array(ct))}"}`;
    } catch {
      /* Never throw, and never silently write plaintext where the caller
         believed it had written ciphertext. Refusing is the only honest
         answer, and md-storage.js's fault path is what surfaces it. */
      return null;
    }
  }

  async function mdOpen(raw: unknown): Promise<unknown> {
    if (typeof raw !== 'string') return raw;
    if (!isEnvelope(raw)) return raw;             // a document written before the seam
    if (!key || !subtle()) return null;

    let env: any = null;
    try { env = JSON.parse(raw); } catch { return null; }
    if (!env || env.v !== MD_CRYPTO_VERSION || env.alg !== MD_CRYPTO_ALG) return null;

    const iv = typeof env.iv === 'string' ? fromB64u(env.iv) : null;
    const ct = typeof env.ct === 'string' ? fromB64u(env.ct) : null;
    if (!iv || !ct || iv.length !== IV_BYTES) return null;

    try {
      const plain = await subtle().decrypt({ name: 'AES-GCM', iv }, key, ct);
      return new TextDecoder().decode(plain);
    } catch {
      return null;                    // wrong key, or the bytes were tampered with
    }
  }

  /* ── the bulk boundary ───────────────────────────────────────────────────
     md-engine.js hands whole flushes through here. The short-circuit returns
     the caller's own array, which is what keeps the seam free while it is off.

     mdOpenAll short-circuits on the absence of a KEY rather than on the flag,
     because with no key there is nothing that could be done with an envelope
     anyway — and checking for one would be the pass over the account this is
     written to avoid. While the seam is off no envelope exists to meet it. */
  async function mdSealAll(pairs: any): Promise<any> {
    if (!sealing || !key || !Array.isArray(pairs)) return pairs;
    const out = new Array(pairs.length);
    for (let i = 0; i < pairs.length; i++) {
      const [k, raw] = pairs[i];
      out[i] = [k, await mdSeal(raw)];
    }
    return out;
  }

  async function mdOpenAll(pairs: any): Promise<any> {
    if (!key || !Array.isArray(pairs)) return pairs;
    const out = new Array(pairs.length);
    for (let i = 0; i < pairs.length; i++) {
      const [k, raw] = pairs[i];
      out[i] = [k, await mdOpen(raw)];
    }
    return out;
  }

  /* ── the key itself ──────────────────────────────────────────────────────
     Extractable, necessarily: wrapping it means exporting the raw bytes and
     encrypting them. It never leaves this module unwrapped, and it is never
     written to storage in any form. */
  async function mdCryptoNewAccountKey(): Promise<any> {
    return subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  }

  async function mdCryptoImportKey(bytes: Uint8Array): Promise<any> {
    return subtle().importKey('raw', bytes, 'AES-GCM', true, ['encrypt', 'decrypt']);
  }

  async function mdCryptoExportKey(k: any): Promise<string> {
    if (!k) return '';
    try { return toB64u(new Uint8Array(await subtle().exportKey('raw', k))); }
    catch { return ''; }
  }

  function mdCryptoArm(k: any): void { key = k || null; sealing = !!k; }
  function mdCryptoDisarm(): void { key = null; sealing = false; }
  /** Stop sealing, keep opening — a rollback that strands nothing. */
  function mdCryptoDisarmSealing(): void { sealing = false; }
  function mdCryptoArmed(): boolean { return sealing; }
  function mdCryptoCanOpen(): boolean { return !!key; }

  /* ── wrapping, once per way in ───────────────────────────────────────────
     HKDF and not PBKDF2: both inputs are already high-entropy — a credential's
     PRF output is 32 random bytes, and the recovery code carries ~124 bits —
     so what is needed is domain separation, not work. The salt is per blob so
     that wrapping the same key twice under the same secret produces two
     different blobs, and the `info` binds the blob to its path. */
  async function derive(secret: BufferSource, salt: Uint8Array, info: string): Promise<any> {
    const material = await subtle().importKey('raw', secret, 'HKDF', false, ['deriveBits']);
    const bits = await subtle().deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt,
        info: new TextEncoder().encode(`mandana:wrap:v${MD_CRYPTO_VERSION}:${info}`),
      },
      material,
      256,
    );
    return subtle().importKey('raw', new Uint8Array(bits), 'AES-GCM', false, ['encrypt', 'decrypt']);
  }

  async function mdCryptoWrapKey(accountKey: any, secret: BufferSource, info: string): Promise<string | null> {
    try {
      const raw = new Uint8Array(await subtle().exportKey('raw', accountKey));
      const salt = randomBytes(SALT_BYTES);
      const iv = randomBytes(IV_BYTES);
      const wrapping = await derive(secret, salt, info);
      const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, wrapping, raw);
      /* `info` travels as a label so a person can be shown which device a blob
         belongs to. It is NOT what unwrapping trusts — the caller supplies the
         info it derives with, and a mismatch fails the GCM tag. */
      return JSON.stringify({
        v: MD_CRYPTO_VERSION,
        alg: MD_CRYPTO_ALG,
        info,
        salt: toB64u(salt),
        iv: toB64u(iv),
        ct: toB64u(new Uint8Array(ct)),
      });
    } catch {
      return null;
    }
  }

  async function mdCryptoUnwrapKey(wrapped: unknown, secret: BufferSource, info: string): Promise<any> {
    if (typeof wrapped !== 'string') return null;
    let blob: any = null;
    try { blob = JSON.parse(wrapped); } catch { return null; }
    if (!blob || blob.v !== MD_CRYPTO_VERSION || blob.alg !== MD_CRYPTO_ALG) return null;

    const salt = typeof blob.salt === 'string' ? fromB64u(blob.salt) : null;
    const iv = typeof blob.iv === 'string' ? fromB64u(blob.iv) : null;
    const ct = typeof blob.ct === 'string' ? fromB64u(blob.ct) : null;
    if (!salt || !iv || !ct || iv.length !== IV_BYTES) return null;

    try {
      const wrapping = await derive(secret, salt, info);
      const raw = await subtle().decrypt({ name: 'AES-GCM', iv }, wrapping, ct);
      return mdCryptoImportKey(new Uint8Array(raw));
    } catch {
      return null;                    // wrong secret, or wrong path
    }
  }

  /* ── the recovery code ───────────────────────────────────────────────────
     Rejection sampling, not modulo. 256 is not a multiple of 31, so `byte % 31`
     would make the first four symbols of the alphabet meaningfully likelier
     than the rest — a bias that costs real entropy in a secret whose entropy is
     the only thing protecting an account nobody can reset. */
  function mdCryptoRecoveryCode(): string {
    let out = '';
    while (out.length < CODE_LENGTH) {
      for (const b of randomBytes(CODE_LENGTH)) {
        if (b >= 248) continue;                   // 248 = 31 × 8, the unbiased range
        out += CODE_ALPHABET[b % CODE_ALPHABET.length];
        if (out.length === CODE_LENGTH) break;
      }
    }
    const groups = [];
    for (let i = 0; i < CODE_LENGTH; i += CODE_GROUP) groups.push(out.slice(i, i + CODE_GROUP));
    return groups.join('-');
  }

  /* What a person typed, reduced to what they meant. Case, spacing, dashes and
     stray whitespace are all noise; anything outside the alphabet is dropped
     rather than rejected, because a code read aloud and typed back is going to
     arrive with a space where a dash was.

     The bytes ARE the canonical characters. There is no stretching here on
     purpose: the stretching that matters is the HKDF in mdCryptoWrapKey, where
     the salt and the path binding also live, and doing half of it here would
     make two places responsible for one derivation. */
  function mdCryptoRecoverySecret(typed: unknown): Uint8Array {
    const text = typeof typed === 'string' ? typed.toUpperCase() : '';
    let canonical = '';
    for (const ch of text) if (CODE_ALPHABET.indexOf(ch) >= 0) canonical += ch;
    return new TextEncoder().encode(canonical);
  }

  Object.assign(g, {
    MD_CRYPTO_VERSION,
    MD_CRYPTO_ALG,
    mdSeal,
    mdOpen,
    mdSealAll,
    mdOpenAll,
    mdCryptoArm,
    mdCryptoArmed,
    mdCryptoCanOpen,
    mdCryptoDisarm,
    mdCryptoDisarmSealing,
    mdCryptoIsEnvelope: isEnvelope,
    mdCryptoNewAccountKey,
    mdCryptoImportKey,
    mdCryptoExportKey,
    mdCryptoWrapKey,
    mdCryptoUnwrapKey,
    mdCryptoRecoveryCode,
    mdCryptoRecoverySecret,
  });

  // CommonJS escape hatch, following md-grant.ts and md-manifest.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = {
      MD_CRYPTO_VERSION, MD_CRYPTO_ALG, mdSeal, mdOpen, mdSealAll, mdOpenAll,
      mdCryptoArm, mdCryptoArmed, mdCryptoCanOpen, mdCryptoDisarm, mdCryptoDisarmSealing,
      mdCryptoIsEnvelope: isEnvelope, mdCryptoNewAccountKey, mdCryptoImportKey,
      mdCryptoExportKey, mdCryptoWrapKey, mdCryptoUnwrapKey,
      mdCryptoRecoveryCode, mdCryptoRecoverySecret,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
